import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { isApprovalRequired, submitForApproval } from "@/lib/approvals";
import { ApprovalRequiredModal } from "@/components/pawkt/ApprovalRequiredModal";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";
import { inr, fdate, fdt } from "@/lib/format";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { SectionCard, KV, CompletionBar, Timeline, DocumentsPanel, MoneyRow, FlagChips } from "@/components/pawkt/three60";
import BusinessOpportunities from "@/components/pawkt/BusinessOpportunities";
import { PackageBalanceWidget } from "@/components/pawkt/PackageCoverage";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BreedSelect } from "@/components/pawkt/BreedSelect";
import { petDateErrors, todayISO } from "@/lib/phone";
import { PetAgeField } from "@/components/pawkt/PetAgeField";
import { petAgeError, petCurrentMonths, ageText, agePrefill } from "@/lib/petAge";
import {
  ArrowLeft, User, PawPrint, Syringe, Stethoscope, Scissors, Home, GraduationCap, Lock, Pencil, HeartPulse, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { NeuterField, NEUTER_LABEL } from "@/components/pawkt/VaccinationRecord";
import { PetCompassPanel } from "@/components/compass/PetCompassPanel";


// Care Passport section schema (structured, non-clinical). Must mirror backend PetIntakeBody.
const PASSPORT = [
  { key: "health", title: "Health", icon: HeartPulse, fields: [
    ["current_medications", "Current medications"], ["previous_surgeries", "Previous surgeries"],
    ["sensitivities", "Sensitivities"], ["special_medical_instructions", "Special medical instructions"],
    ["primary_veterinarian", "Primary vet"], ["veterinary_clinic", "Vet clinic"], ["emergency_vet_contact", "Emergency vet contact"]] },
  { key: "feeding", title: "Feeding", icon: PawPrint, fields: [
    ["food_type", "Food type"], ["brand", "Brand"], ["schedule", "Schedule"], ["quantity", "Quantity"],
    ["treats", "Treats"], ["food_allergies", "Food allergies"], ["special_diet", "Special diet"], ["feeding_instructions", "Feeding instructions"]] },
  { key: "behaviour", title: "Behaviour", icon: PawPrint, fields: [
    ["energy_level", "Energy level"], ["socialization", "Socialization"], ["good_with_children", "Good with children"],
    ["fear_triggers", "Fear triggers"], ["handling_sensitivity", "Handling sensitivity"], ["grooming_sensitivity", "Grooming sensitivity"],
    ["nail_bathing_sensitivity", "Nail / bathing sensitivity"], ["behavioural_triggers", "Behavioural triggers"], ["notes", "Notes"]] },
  { key: "boarding", title: "Boarding & Daycare", icon: Home, fields: [
    ["sleeping_preference", "Sleeping preference"], ["crate_preference", "Crate preference"], ["play_preference", "Play preference"],
    ["toilet_routine", "Toilet routine"], ["walking_routine", "Walking routine"], ["separation_instructions", "Separation instructions"],
    ["emergency_handling_instructions", "Emergency handling"]] },
  { key: "owner_prefs", title: "Owner preferences", icon: User, fields: [
    ["preferred_communication_channel", "Preferred channel"], ["preferred_veterinarian", "Preferred vet"], ["special_instructions", "Special instructions"]] },
];

export default function PetDetail() {
  const { id } = useParams();
  const [tabSp] = useSearchParams();
  const tabParam = tabSp.get("tab");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [vaxOpen, setVaxOpen] = useState(false);
  const [editPetOpen, setEditPetOpen] = useState(tabSp.get("edit") === "1");
  const [removeOpen, setRemoveOpen] = useState(false);
  const [petApprovalOpen, setPetApprovalOpen] = useState(false);
  const [pendingPetEdit, setPendingPetEdit] = useState(null);
  const submitPetApproval = useMutation({
    mutationFn: () => submitForApproval("pets.edit", id, pendingPetEdit),
    onSuccess: () => { setPetApprovalOpen(false); toast.success("Change submitted for Owner approval."); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const { data, isLoading } = useQuery({ queryKey: ["pet360", id], queryFn: async () => (await api.get(`/pets/${id}/360`)).data });
  const { data: intakeData } = useQuery({ queryKey: ["intake", id], queryFn: async () => (await api.get(`/pets/${id}/intake`)).data });
  const canEdit = hasPerm(user, "pets.edit");
  const canDelete = hasPerm(user, "pets.delete");
  if (isLoading || !data) return <Loading />;
  const p = data.pet, sp = data.spend;
  const intake = intakeData?.intake || {};
  const completion = intakeData?.completion?.percent ?? data.profile_completion;
  // Clinical add: requires vet.clinical AND vet module/view (vaccinations.available implies module+view).
  const canAddVax = hasPerm(user, "vet.clinical") && data.vaccinations?.available;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-pawkt-text-secondary hover:text-pawkt-text mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

      {/* Header */}
      <div className="rounded-xl border border-pawkt-border bg-white p-6 shadow-card" data-testid="pet360-header">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="h-20 w-20 rounded-2xl bg-pawkt-primary-soft flex items-center justify-center overflow-hidden shrink-0">
            {p.photo ? <img src={p.photo} alt="" className="h-full w-full object-cover" /> : <PawPrint className="h-9 w-9 text-pawkt-primary" />}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-pawkt-text">{p.name}</h1>
            <p className="text-sm text-pawkt-text-secondary">{[p.species, p.breed].filter(Boolean).join(" · ")}</p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-pawkt-text-disabled">
              <span data-testid="pet-age">{petCurrentMonths(p) != null ? `${ageText(petCurrentMonths(p))} old${!p.dob && p.age_dob ? " (approx.)" : ""}` : "Age unknown — add it via Edit"}</span><span>{p.gender || "—"}</span>
              {p.dob ? <span data-testid="pet-dob">Born {fdate(p.dob)}</span> : null}
              {p.adoption_date ? <span data-testid="pet-adoption">Adopted {fdate(p.adoption_date)}</span> : null}
              {p.must_board_alone ? <span className="rounded bg-amber-100 px-1.5 text-amber-800" data-testid="pet-board-alone">Boards alone</span> : null}
              {p.weight ? <span>{p.weight} kg</span> : null}{p.color ? <span>{p.color}</span> : null}
              {p.microchip ? <span className="font-mono">chip {p.microchip}</span> : null}
              <span className={`rounded px-1.5 ${p.neuter_status ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-800"}`} data-testid="pet-neuter-status">Spay/neuter: {p.neuter_status ? NEUTER_LABEL[p.neuter_status] : "not recorded"}{p.neuter_date ? ` (${fdate(p.neuter_date)})` : ""}</span>
            </div>
            <FlagChips flags={[
              { label: "Allergies", value: p.allergies }, { label: "Conditions", value: p.medical_conditions }, { label: "Temperament", value: p.temperament },
            ]} />
          </div>
          <div className="w-full sm:w-52 space-y-3">
            <CompletionBar percent={completion} testid="pet-completion" />
            {data.owner && <Button size="sm" variant="outline" onClick={() => navigate(`/customers/${data.owner.id}`)} data-testid="pet-owner-btn" className="w-full gap-1 min-h-[44px]"><User className="h-4 w-4" /> {data.owner.name}</Button>}
            {canEdit && <Button size="sm" onClick={() => setEditPetOpen(true)} data-testid="pet-edit-btn" className="w-full gap-1 min-h-[44px]"><Pencil className="h-4 w-4" /> Edit pet</Button>}
            <PetCompassPanel petId={id} canSave={canEdit} />
            {canDelete && <Button size="sm" variant="outline" onClick={() => setRemoveOpen(true)} data-testid="pet-remove-btn" className="w-full gap-1 min-h-[44px] text-pawkt-emergency border-pawkt-coral hover:bg-pawkt-coral/20"><Trash2 className="h-4 w-4" /> Remove pet</Button>}
            <ApprovalRequestButton action="pets.delete" targetId={p.id} targetLabel={p.name} className="w-full justify-center" />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        <div className="space-y-5">
          <SectionCard title="Spend" icon={HeartPulse} testid="pet-spend">
            <MoneyRow billed={sp.service_revenue} paid={sp.lifetime_revenue} outstanding={0} avg={0} />
            <p className="text-[11px] text-pawkt-text-disabled mt-2">Service value from completed bookings · lifetime revenue rollup. Not recomputed client-side.</p>
            {!!sp.service_mix?.length && (
              <div className="mt-3 space-y-1">
                {sp.service_mix.slice(0, 5).map((s) => (
                  <div key={s.service} className="flex justify-between text-sm"><span className="text-pawkt-text-secondary truncate">{s.service}</span><span className="font-medium">{inr(s.revenue)}</span></div>
                ))}
              </div>
            )}
          </SectionCard>
          <DocumentsPanel base={`/pets/${id}`} canUpload={canEdit} testid="pet-documents" />
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-xl border border-pawkt-border bg-white p-5 shadow-card">
            <Tabs defaultValue={tabParam === "vaccinations" ? "vaccinations" : "passport"}>
              <TabsList className="bg-pawkt-elevated flex-wrap h-auto">
                <TabsTrigger value="passport" data-testid="pet-tab-passport">Care Passport</TabsTrigger>
                <TabsTrigger value="vaccinations" data-testid="pet-tab-vax">Vaccinations</TabsTrigger>
                <TabsTrigger value="vet" data-testid="pet-tab-vet">Veterinary</TabsTrigger>
                <TabsTrigger value="grooming" data-testid="pet-tab-grooming">Grooming</TabsTrigger>
                <TabsTrigger value="boarding" data-testid="pet-tab-boarding">Boarding</TabsTrigger>
                <TabsTrigger value="training" data-testid="pet-tab-training">Training</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="pet-tab-timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="passport" className="mt-4 space-y-4">
                <BusinessOpportunities scope="pet" petId={id} customerId={data.owner?.id} />
                {data.owner?.id && <PackageBalanceWidget customerId={data.owner.id} petId={id} title="Package coverage" />}
                <div className="flex justify-end">
                  {canEdit && <Button size="sm" onClick={() => setEditOpen(true)} data-testid="passport-edit-btn" className="gap-1"><Pencil className="h-4 w-4" /> Edit passport</Button>}
                </div>
                {PASSPORT.map((sec) => {
                  const d = intake[sec.key] || {};
                  const any = sec.fields.some(([k]) => d[k]);
                  return (
                    <SectionCard key={sec.key} title={sec.title} icon={sec.icon} testid={`passport-${sec.key}`}>
                      {!any ? <p className="text-sm text-pawkt-text-disabled">Not recorded yet.</p> :
                        sec.fields.filter(([k]) => d[k]).map(([k, l]) => <KV key={k} label={l} value={String(d[k])} />)}
                    </SectionCard>
                  );
                })}
              </TabsContent>

              <TabsContent value="vaccinations" className="mt-4">
                {canAddVax && (
                  <div className="flex justify-end mb-3">
                    <Button size="sm" onClick={() => setVaxOpen(true)} data-testid="vax-add-btn" className="gap-1"><Syringe className="h-4 w-4" /> Add vaccination</Button>
                  </div>
                )}
                <VaxList section={data.vaccinations} />
              </TabsContent>

              <TabsContent value="vet" className="mt-4">
                <VetSection section={data.veterinary} />
              </TabsContent>

              <TabsContent value="grooming" className="mt-4">
                <VerticalHistory section={data.grooming} kind="grooming" />
              </TabsContent>

              <TabsContent value="boarding" className="mt-4">
                <VerticalHistory section={data.boarding} kind="boarding" />
              </TabsContent>

              <TabsContent value="training" className="mt-4">
                <TrainingSection section={data.training} />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <Timeline base={`/pets/${id}`} testid="pet-timeline" />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {canEdit && <PassportEditor open={editOpen} onOpenChange={setEditOpen} id={id} intake={intake}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["intake", id] }); qc.invalidateQueries({ queryKey: ["pet360", id] }); }} />}
      {canAddVax && <AddVaccinationSheet open={vaxOpen} onOpenChange={setVaxOpen} id={id} species={p.species} customerId={p.customer_id}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["pet360", id] }); qc.invalidateQueries({ queryKey: ["docs", `/pets/${id}`] }); qc.invalidateQueries({ queryKey: ["timeline", `/pets/${id}`] }); }} />}
      {canEdit && <EditPetSheet open={editPetOpen} onOpenChange={setEditPetOpen} pet={p} passportNeuter={intake.basics?.spayed_neutered || intake.spayed_neutered || ""}
        onApprovalRequired={(body) => { setPendingPetEdit(body); setEditPetOpen(false); setPetApprovalOpen(true); }}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["pet360", id] }); qc.invalidateQueries({ queryKey: ["intake", id] }); }} />}
      <ApprovalRequiredModal open={petApprovalOpen} onOpenChange={setPetApprovalOpen} entity="pet change"
        busy={submitPetApproval.isPending} testid="pet-approval-modal" onSubmit={() => submitPetApproval.mutate()} />
      {canDelete && <RemovePetDialog open={removeOpen} onOpenChange={setRemoveOpen} pet={p} ownerName={data.owner?.name}
        onRemoved={() => { qc.invalidateQueries({ queryKey: ["pets"] }); navigate(data.owner ? `/customers/${data.owner.id}` : "/pets"); }} />}
    </div>
  );
}

function EditPetSheet({ open, onOpenChange, pet, onSaved, onApprovalRequired, passportNeuter }) {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setV({
      customer_id: pet.customer_id, name: pet.name || "", species: pet.species || "",
      species_id: pet.species_id || "", breed: pet.breed || "", breed_id: pet.breed_id || "",
      breed_canonical: pet.breed_canonical || "", gender: pet.gender || "", dob: pet.dob || "",
      adoption_date: pet.adoption_date || "", must_board_alone: !!pet.must_board_alone,
      weight: pet.weight ?? "", color: pet.color || "", microchip: pet.microchip || "",
      allergies: pet.allergies || "", medical_conditions: pet.medical_conditions || "",
      temperament: pet.temperament || "", dietary: pet.dietary || "", notes: pet.notes || "",
      neuter_status: pet.neuter_status || "", neuter_date: pet.neuter_date || "", neuter_details: pet.neuter_details || "",
      ...agePrefill(pet),
    });
    /* eslint-disable-next-line */
  }, [open]);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  async function submit(e) {
    e.preventDefault();
    if (!(v.name || "").trim()) { toast.error("Pet name is required"); return; }
    const de = petDateErrors(v);
    if (de.dob || de.adoption_date) { toast.error(de.dob || de.adoption_date); return; }
    const ae = petAgeError(v);
    if (ae) { toast.error(ae); return; }
    if (!v.neuter_status) { toast.error("Select spayed/neutered status (Yes, No or Unknown)"); return; }
    setBusy(true);
    try {
      const body = { ...v, weight: v.weight === "" || v.weight == null ? null : Number(v.weight) };
      await api.put(`/pets/${pet.id}`, body);
      toast.success("Pet updated"); onSaved(); onOpenChange(false);
    } catch (err) {
      if (isApprovalRequired(err)) {
        const body = { ...v, weight: v.weight === "" || v.weight == null ? null : Number(v.weight) };
        onApprovalRequired?.(body);
      } else toast.error(apiErr(err));
    } finally { setBusy(false); }
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="pet-edit-sheet">
        <SheetHeader><SheetTitle>Edit pet</SheetTitle></SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-semibold text-pawkt-text-secondary">Name</label>
            <Input value={v.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="pet-edit-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <BreedSelect v={v} set={set} setMany={(patch) => setV((s) => ({ ...s, ...patch }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-pawkt-text-secondary">Gender</label>
              <select value={v.gender || ""} onChange={(e) => set("gender", e.target.value)} data-testid="pet-edit-gender"
                className="mt-1 w-full rounded-lg border border-pawkt-border bg-white px-3 py-2 text-sm min-h-[44px]">
                <option value="">Gender</option><option value="Male">Male</option><option value="Female">Female</option>
              </select>
            </div>
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Date of birth</label>
              <Input type="date" max={todayISO()} value={v.dob || ""} onChange={(e) => set("dob", e.target.value)} className="mt-1" data-testid="pet-edit-dob" /></div>
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Adoption date</label>
              <Input type="date" max={todayISO()} value={v.adoption_date || ""} onChange={(e) => set("adoption_date", e.target.value)} className="mt-1" data-testid="pet-edit-adoption" /></div>
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Weight (kg)</label>
              <Input type="number" step="0.1" value={v.weight ?? ""} onChange={(e) => set("weight", e.target.value)} className="mt-1" data-testid="pet-edit-weight" /></div>
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Colour / markings</label>
              <Input value={v.color || ""} onChange={(e) => set("color", e.target.value)} className="mt-1" data-testid="pet-edit-color" /></div>
          </div>
          {(petDateErrors(v).dob || petDateErrors(v).adoption_date) && <p className="text-xs text-rose-600" data-testid="pet-edit-date-error">{petDateErrors(v).dob || petDateErrors(v).adoption_date}</p>}
          <PetAgeField value={v} testid="pet-edit-age" showError onChange={(p) => setV((s) => ({ ...s, ...p }))} />
          <label className="flex items-center gap-2 text-sm text-pawkt-text"><input type="checkbox" checked={!!v.must_board_alone} onChange={(e) => set("must_board_alone", e.target.checked)} data-testid="pet-edit-board-alone" /> Must board alone (never shares a kennel/room)</label>
          <NeuterField value={v} onChange={(p) => setV((s) => ({ ...s, ...p }))} testid="pet-edit-neuter" />
          {passportNeuter && <p className="text-[11px] text-slate-500" data-testid="pet-edit-passport-neuter">Care passport note (read-only, not copied): {passportNeuter}</p>}
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Microchip</label>
            <Input value={v.microchip || ""} onChange={(e) => set("microchip", e.target.value)} className="mt-1" data-testid="pet-edit-microchip" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Allergies</label>
            <Input value={v.allergies || ""} onChange={(e) => set("allergies", e.target.value)} className="mt-1" data-testid="pet-edit-allergies" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Medical conditions</label>
            <Input value={v.medical_conditions || ""} onChange={(e) => set("medical_conditions", e.target.value)} className="mt-1" data-testid="pet-edit-conditions" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Temperament</label>
            <Input value={v.temperament || ""} onChange={(e) => set("temperament", e.target.value)} className="mt-1" data-testid="pet-edit-temperament" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Notes</label>
            <Textarea rows={2} value={v.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1" data-testid="pet-edit-notes" /></div>
          <SheetFooter><Button type="submit" disabled={busy} data-testid="pet-edit-save-btn" className="min-h-[44px]">{busy ? "Saving…" : "Save changes"}</Button></SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function RemovePetDialog({ open, onOpenChange, pet, ownerName, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  useEffect(() => { if (open) setBlocked(null); }, [open]);
  async function remove() {
    setBusy(true);
    try {
      await api.delete(`/pets/${pet.id}`);
      toast.success(`${pet.name} removed`); onRemoved(); onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "PET_HAS_HISTORY") setBlocked(d);
      else toast.error(apiErr(err));
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="pet-remove-dialog">
        <DialogHeader><DialogTitle className="font-display">Remove pet</DialogTitle></DialogHeader>
        {blocked ? (
          <div className="space-y-3" data-testid="pet-remove-blocked">
            <p className="text-sm text-pawkt-text">{blocked.message}</p>
            <ul className="text-xs text-pawkt-text-secondary list-disc pl-5">
              {Object.entries(blocked.references || {}).map(([k, n]) => <li key={k}>{n} {k}</li>)}
            </ul>
            <p className="text-sm text-pawkt-text-secondary">Use <strong>Edit pet</strong> to correct the details instead.</p>
          </div>
        ) : (
          <p className="text-sm text-pawkt-text">Remove <strong>{pet.name}</strong>{ownerName ? <> from <strong>{ownerName}</strong>'s pets</> : ""}? This permanently deletes the pet profile and can't be undone.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">{blocked ? "Close" : "Cancel"}</Button>
          {!blocked && <Button onClick={remove} disabled={busy} data-testid="pet-remove-confirm-btn" className="min-h-[44px] bg-pawkt-emergency hover:bg-pawkt-emergency/90 text-white">{busy ? "Removing…" : "Remove pet"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddVaccinationSheet({ open, onOpenChange, id, species, customerId, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState(false);
  const { data: cat } = useQuery({
    queryKey: ["vaxcat", species],
    queryFn: async () => (await api.get("/vaccination-catalog", { params: { species: species || "" } })).data,
    enabled: open,
  });
  const options = cat?.items || [];

  async function submit(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    const vaccine = (custom ? f.get("vaccine_custom") : f.get("vaccine_select")) || "";
    const administered = f.get("administered_date");
    const due = f.get("next_due_date");
    if (!vaccine) { toast.error("Select a vaccine"); return; }
    if (!administered) { toast.error("Administered date is required"); return; }
    if (due && due < administered) { toast.error("Next due date cannot be before administered date"); return; }
    setBusy(true);
    try {
      await api.post("/veterinary/vaccinations", {
        pet_id: id, customer_id: customerId, vaccine,
        administered_date: administered, next_due_date: due || "",
        provider: f.get("provider") || "", batch_no: f.get("batch_no") || "", notes: f.get("notes") || "",
      });
      const file = f.get("file");
      if (file && file.name) {
        const fd = new FormData();
        fd.append("file", file); fd.append("doc_type", "vaccination_certificate");
        fd.append("label", `${vaccine} certificate`);
        await api.post(`/pets/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Vaccination recorded"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="vax-sheet">
        <SheetHeader><SheetTitle>Add vaccination</SheetTitle></SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-semibold text-pawkt-text-secondary">Vaccine ({species || "any"})</label>
            {!custom ? (
              <select name="vaccine_select" data-testid="vax-select" className="mt-1 w-full rounded-lg border border-pawkt-border bg-white px-3 py-2 text-sm">
                {options.map((o) => <option key={o.id} value={o.name}>{o.name}{o.source === "custom" ? " (custom)" : ""}</option>)}
              </select>
            ) : <Input name="vaccine_custom" placeholder="Vaccine name" className="mt-1" data-testid="vax-custom-input" />}
            <button type="button" onClick={() => setCustom((c) => !c)} className="text-xs text-pawkt-primary mt-1" data-testid="vax-toggle-custom">
              {custom ? "Choose from catalog" : "Enter a different vaccine"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Administered *</label><Input type="date" name="administered_date" required className="mt-1" data-testid="vax-administered" /></div>
            <div><label className="text-xs font-semibold text-pawkt-text-secondary">Next due</label><Input type="date" name="next_due_date" className="mt-1" data-testid="vax-nextdue" /></div>
          </div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Provider / clinic</label><Input name="provider" className="mt-1" data-testid="vax-provider" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Batch / lot</label><Input name="batch_no" className="mt-1" data-testid="vax-batch" /></div>
          <div><label className="text-xs font-semibold text-pawkt-text-secondary">Notes</label><Textarea name="notes" rows={2} className="mt-1" data-testid="vax-notes" /></div>
          <div>
            <label className="text-xs font-semibold text-pawkt-text-secondary">Certificate (PDF / image, optional)</label>
            <input type="file" name="file" accept=".pdf,image/jpeg,image/png,image/webp" className="mt-1 w-full text-sm" data-testid="vax-file" />
          </div>
          <SheetFooter><Button type="submit" disabled={busy} data-testid="vax-save-btn">{busy ? "Saving…" : "Save vaccination"}</Button></SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function vaxState(v) {
  if (v.status && /overdue/i.test(v.status)) return "overdue";
  const due = v.next_due_date; if (!due) return v.status || "recorded";
  const days = (new Date(due) - Date.now()) / 8.64e7;
  if (days < 0) return "overdue"; if (days < 30) return "due_soon"; return "up_to_date";
}
function VaxBadge({ state }) {
  const map = {
    up_to_date: ["Up to date", "bg-pawkt-mint text-pawkt-success-deep"],
    due_soon: ["Due soon", "bg-pawkt-butter text-pawkt-secondary-deep"],
    overdue: ["Overdue", "bg-pawkt-coral text-pawkt-emergency"],
  };
  const [label, cls] = map[state] || [String(state || "recorded").replace(/_/g, " "), "bg-pawkt-elevated text-pawkt-text-secondary"];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${cls}`} data-testid={`vax-status-${state}`}>{label}</span>;
}
function VaxList({ section }) {
  if (!section.available) return <RestrictedState reason={section.reason} what="vaccination" />;
  if (!section.items?.length) return <Empty title="No vaccination records available" icon={Syringe} />;
  const rows = [...section.items].sort((a, b) => String(a.administered_date || a.next_due_date || a.created_at || "").localeCompare(String(b.administered_date || b.next_due_date || b.created_at || "")));
  return (
    <div className="divide-y divide-pawkt-border" data-testid="pet-vax">
      {rows.map((v, i) => {
        const vs = v.vaccination_status || "administered";
        const st = vs === "administered" ? vaxState(v) : vs;
        return (
          <div key={v.id || i} className="flex items-center gap-3 py-2.5 text-sm">
            <Syringe className="h-4 w-4 text-pawkt-primary shrink-0" />
            <div className="flex-1"><div className="font-medium text-pawkt-text">{v.vaccine || v.vaccine_type || v.name || "Vaccine"}</div>
              <div className="text-xs text-pawkt-text-disabled">{v.administered_date ? `Given ${fdate(v.administered_date)}` : "Not given"}{v.next_due_date ? ` · due ${fdate(v.next_due_date)}` : ""}{v.provider ? ` · ${v.provider}` : ""}{v.batch_no ? ` · lot ${v.batch_no}` : ""}{v.source === "booking" ? " · recorded at booking" : ""}{v.needs_clinical_review ? " · needs clinical review" : ""}</div></div>
            <VaxBadge state={st} />
          </div>
        );
      })}
    </div>
  );
}

function VetSection({ section }) {
  if (!section.available) return <RestrictedState reason={section.reason} what="veterinary" count={section.visit_count} />;
  const groups = [["encounters", "Visits", Stethoscope], ["diagnoses", "Diagnoses"], ["procedures", "Procedures"],
    ["medications", "Medications"], ["labs", "Labs"], ["imaging", "Imaging"], ["emergencies", "Emergencies"]];
  const total = groups.reduce((n, [k]) => n + (section[k]?.length || 0), 0);
  if (!total) return <Empty title="No veterinary records" icon={Stethoscope} />;
  return (
    <div className="space-y-4" data-testid="pet-vet">
      {groups.map(([k, label]) => (section[k]?.length ? (
        <div key={k}>
          <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1">{label}</div>
          <div className="divide-y divide-pawkt-border">
            {section[k].map((r, i) => (
              <div key={r.id || i} className="flex items-center gap-3 py-2 text-sm">
                <div className="flex-1 text-pawkt-text">{r.condition_name || r.chief_complaint || r.procedure_name || r.medication_name || r.test_name || r.imaging_type || r.description || "Record"}</div>
                <div className="text-xs text-pawkt-text-disabled">{fdate(r.visit_date || r.created_at)}</div>
                {r.status && <StatusBadge status={r.status} />}
              </div>
            ))}
          </div>
        </div>
      ) : null))}
    </div>
  );
}

function VerticalHistory({ section, kind }) {
  const Icon = kind === "grooming" ? Scissors : Home;
  if (!section.available) return <RestrictedState reason={section.reason} what={kind} />;
  if (!section.items?.length) return <Empty title={`No ${kind} history yet`} icon={Icon} />;
  return (
    <div className="divide-y divide-pawkt-border" data-testid={`pet-${kind}`}>
      {section.items.map((s, i) => (
        <div key={s.id || i} className="flex items-center gap-3 py-2.5 text-sm">
          <Icon className="h-4 w-4 text-pawkt-primary shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-pawkt-text">{kind === "grooming" ? (s.service_name || "Grooming") : `${fdate(s.check_in)} → ${fdate(s.expected_check_out || s.actual_check_out)}`}</div>
            <div className="text-xs text-pawkt-text-disabled">{fdt(s.start_time || s.check_in)}{s.staff_name ? ` · ${s.staff_name}` : ""}{s.notes ? ` · ${s.notes}` : ""}</div>
          </div>
          {s.total ? <span className="text-pawkt-text">{inr(s.total)}</span> : null}
          <StatusBadge status={s.status || "completed"} />
        </div>
      ))}
    </div>
  );
}

function TrainingSection({ section }) {
  if (!section.available) return <RestrictedState reason={section.reason} what="training" />;
  const pkgs = section.packages || [], sess = section.sessions || [];
  if (!pkgs.length && !sess.length) return <Empty title="No training records" icon={GraduationCap} />;
  return (
    <div className="space-y-4" data-testid="pet-training">
      {!!pkgs.length && <div><div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1">Packages</div>
        <div className="divide-y divide-pawkt-border">{pkgs.map((p, i) => (
          <div key={p.id || i} className="flex items-center gap-3 py-2 text-sm"><div className="flex-1 text-pawkt-text">{p.package_type || p.name || "Package"}</div>
            <span className="text-xs text-pawkt-text-disabled">{p.sessions_remaining ?? p.sessions_purchased ?? 0} sessions</span><StatusBadge status={p.status || "active"} /></div>))}</div></div>}
      {!!sess.length && <div><div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary mb-1">Sessions</div>
        <div className="divide-y divide-pawkt-border">{sess.map((s, i) => (
          <div key={s.id || i} className="flex items-center gap-3 py-2 text-sm"><div className="flex-1 text-pawkt-text">{s.focus || "Session"}</div>
            <span className="text-xs text-pawkt-text-disabled">{fdate(s.scheduled_at)}</span><StatusBadge status={s.status || "scheduled"} /></div>))}</div></div>}
    </div>
  );
}

function RestrictedState({ reason, what, count }) {
  const map = {
    module_disabled: { icon: Lock, title: `${what} module not enabled`, sub: "Enable this module in Settings to record and view history." },
    restricted: { icon: Lock, title: "Restricted", sub: `You don't have access to ${what} information.` },
    clinical_restricted: { icon: Lock, title: "Clinical details restricted", sub: `${count || 0} visit(s) on record. Veterinary clinical access is required to view details.` },
  };
  const m = map[reason] || map.restricted;
  return <Empty title={m.title} subtitle={m.sub} icon={m.icon} />;
}

function PassportEditor({ open, onOpenChange, id, intake, onSaved }) {
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    const body = {};
    PASSPORT.forEach((sec) => {
      body[sec.key] = {};
      sec.fields.forEach(([k]) => { const v = f.get(`${sec.key}.${k}`); if (v != null) body[sec.key][k] = v; });
    });
    setBusy(true);
    try {
      const exists = Object.keys(intake || {}).length > 0 && intake.id;
      if (exists) await api.put(`/pets/${id}/intake`, body);
      else await api.post(`/pets/${id}/intake`, body);
      toast.success("Care Passport saved"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="passport-editor">
        <SheetHeader><SheetTitle>Edit Care Passport</SheetTitle></SheetHeader>
        <form onSubmit={submit} className="space-y-5 mt-4">
          {PASSPORT.map((sec) => (
            <div key={sec.key}>
              <div className="flex items-center gap-2 mb-2"><sec.icon className="h-4 w-4 text-pawkt-primary" /><h4 className="font-display font-bold text-pawkt-text">{sec.title}</h4></div>
              <div className="space-y-2">
                {sec.fields.map(([k, l]) => {
                  const dv = (intake[sec.key] || {})[k] || "";
                  const long = /instructions|notes|triggers|diet/.test(k);
                  return (
                    <div key={k}>
                      <label className="text-xs text-pawkt-text-secondary">{l}</label>
                      {long
                        ? <Textarea name={`${sec.key}.${k}`} defaultValue={dv} rows={2} className="mt-0.5" data-testid={`pf-${sec.key}-${k}`} />
                        : <Input name={`${sec.key}.${k}`} defaultValue={dv} className="mt-0.5" data-testid={`pf-${sec.key}-${k}`} />}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <SheetFooter><Button type="submit" disabled={busy} data-testid="passport-save-btn">{busy ? "Saving…" : "Save passport"}</Button></SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
